import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, EmptyState, Input, Loading, Modal } from '@/components/ui';
import { FileText } from 'lucide-react';
import {
  amountToAgorot, benefitError, isBenefitForbidden, jerusalemToday, orgPriceBenefitsApi,
  type BenefitDuration, type BenefitInput, type BenefitKind, type BenefitPreview,
  type BenefitsApi, type OrgPriceBenefit, type Price,
} from '@/api/orgPriceBenefits';

const money = (amount: number) => new Intl.NumberFormat('he-IL', {
  style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(amount / 100);
const date = (iso: string) => new Intl.DateTimeFormat('he-IL', {
  timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric',
}).format(new Date(iso));
const selectClass = 'w-full min-h-11 rounded-lg border border-navy-border bg-navy-deeper px-3 py-2 text-sm text-ink-primary focus:ring-2 focus:ring-info';

type Draft = {
  kind: BenefitKind; value: string; start: string; duration: BenefitDuration;
  end: string; cycles: string; name: string; note: string;
};
const freshDraft = (): Draft => ({ kind: 'absolute', value: '', start: jerusalemToday(), duration: 'fixed_term', end: '', cycles: '', name: '', note: '' });

export function benefitDraftInput(draft: Draft): BenefitInput | null {
  const value = draft.kind === 'free' ? null : draft.kind === 'percent'
    ? (/^\d+(?:[.]\d+)?$/.test(draft.value) && Number(draft.value) > 0 && Number(draft.value) <= 100 ? draft.value : null)
    : amountToAgorot(draft.value);
  if (draft.kind !== 'free' && value === null) return null;
  if (!draft.start || (draft.duration === 'fixed_term' && (!draft.end || draft.end < draft.start))) return null;
  if (draft.duration === 'billing_cycles' && (!/^\d+$/.test(draft.cycles) || Number(draft.cycles) < 1 || Number(draft.cycles) > 2147483647)) return null;
  return {
    benefit_type: draft.kind, benefit_value: value, starts_on: draft.start,
    duration_mode: draft.duration, ends_on: draft.duration === 'fixed_term' ? draft.end : null,
    billing_cycles: draft.duration === 'billing_cycles' ? Number(draft.cycles) : null,
    reason_customer: draft.name.trim(), reason_internal: draft.note.trim(),
  };
}

function Receipt({ price, name }: { price: Price; name: string }) {
  const rows: Array<[string, number, boolean?]> = [
    ['מחיר מחירון', price.catalog_price_agorot], [name || 'הטבה חדשה', price.benefit_amount_agorot],
    ...(price.addons_total_agorot ? [['תוספים', price.addons_total_agorot] as [string, number]] : []),
    ['לפני מע״מ', price.subtotal_agorot], [`מע״מ (${price.vat_percent}%)`, price.vat_agorot],
    ['סה״כ כולל מע״מ', price.total_agorot, true],
  ];
  return <dl className="space-y-1.5">{rows.map(([label, amount, total], index) => <div key={index}
    className={`flex items-baseline justify-between gap-4 ${total ? 'border-t border-navy-border pt-2 font-semibold' : ''}`}>
    <dt>{label}</dt><dd className="shrink-0"><bdi>{money(amount)}</bdi></dd>
  </div>)}</dl>;
}

function benefitValue(row: OrgPriceBenefit): string {
  if (row.display_type === 'amount_off' && row.amount_off_agorot !== null) return `הנחה בסכום ${money(row.amount_off_agorot)} · מחיר שנשמר ${money(Number(row.benefit_value))} + מע״מ`;
  if (row.benefit_type === 'free') return 'חינם';
  if (row.benefit_type === 'percent') return `${row.benefit_value}% הנחה`;
  if (row.benefit_type === 'plan_price_of') return `מחיר מסלול ${row.benefit_value}`;
  return `${money(Number(row.benefit_value))} + מע״מ`;
}

function benefitDuration(row: OrgPriceBenefit): string {
  if (row.duration_mode === 'billing_cycles') {
    const remaining = Math.max(0, (row.billing_cycles ?? 0) - row.cycles_used);
    return remaining === 1 ? 'עוד חיוב אחד' : `עוד ${remaining} חיובים`;
  }
  if (row.duration_mode === 'until_cancelled' || row.duration_mode === 'perpetual') return 'ללא סיום';
  return row.ends_at ? `עד ${date(row.ends_at)}` : 'ללא סיום';
}

function Confirmation({ title, children, pending, confirm, cancel, confirmLabel }: {
  title: string; children: ReactNode; pending: boolean; confirm(): void; cancel(): void; confirmLabel: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    box.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => previous?.focus();
  }, []);
  return <Modal isOpen onClose={() => { if (!pending) cancel(); }} size="md"><div ref={box}
    role="alertdialog" aria-modal="true" aria-label={title} dir="rtl" className="space-y-4 py-2"
    onKeyDown={event => {
      if (event.key !== 'Tab') return;
      const buttons = box.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
      if (!buttons?.length) return;
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }}>
    <h3 className="text-lg font-semibold">{title}</h3>{children}
    <div className="flex flex-col gap-3 sm:flex-row">
      <Button variant="danger" className="min-h-11" loading={pending} onClick={confirm}>{confirmLabel}</Button>
      <Button variant="secondary" className="min-h-11" disabled={pending} onClick={cancel}>ביטול</Button>
    </div>
  </div></Modal>;
}

export default function OrgPriceBenefitsPanel({ orgId, api = orgPriceBenefitsApi }: { orgId: string; api?: BenefitsApi }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(freshDraft);
  const [previewInput, setPreviewInput] = useState<BenefitInput | null>(null);
  const [replace, setReplace] = useState<{ input: BenefitInput; preview: BenefitPreview } | null>(null);
  const [ending, setEnding] = useState<OrgPriceBenefit | null>(null);
  const [actionError, setActionError] = useState('');
  const input = useMemo(() => benefitDraftInput(draft), [draft]);
  const inputKey = JSON.stringify(input);
  useEffect(() => {
    const timer = setTimeout(() => setPreviewInput(input), 300);
    return () => clearTimeout(timer);
  }, [input, orgId]);
  useEffect(() => { setDraft(freshDraft()); setPreviewInput(null); setReplace(null); setEnding(null); setActionError(''); }, [orgId]);

  const overview = useQuery({ queryKey: ['admin-org-price-benefits', orgId], queryFn: () => api.list(orgId), retry: false });
  const preview = useQuery({
    queryKey: ['admin-org-price-benefit-preview', orgId, previewInput],
    queryFn: () => api.preview(orgId, previewInput!), enabled: overview.isSuccess && previewInput !== null, retry: false,
  });
  const invalidate = async (savedOrg: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['admin-org-price-benefits', savedOrg] }),
      qc.invalidateQueries({ queryKey: ['admin-commercial-agreement', savedOrg] }),
      qc.invalidateQueries({ queryKey: ['admin-org-subs', savedOrg] }),
      qc.invalidateQueries({ queryKey: ['admin-org-price-benefit-preview', savedOrg] }),
    ]);
  };
  const save = useMutation({
    mutationFn: (data: { org: string; input: BenefitInput; preview: BenefitPreview; replaceIds: string[] }) =>
      api.save(data.org, data.input, data.preview.fingerprint, data.replaceIds),
    onSuccess: async (_, data) => { await invalidate(data.org); setReplace(null); setDraft(freshDraft()); toast.success('ההטבה נשמרה'); },
    onError: (error) => { setActionError(benefitError(error)); setReplace(null); void preview.refetch(); },
  });
  const end = useMutation({
    mutationFn: (data: { org: string; id: string }) => api.end(data.org, data.id),
    onSuccess: async (_, data) => { await invalidate(data.org); setEnding(null); toast.success('ההטבה הסתיימה'); },
    onError: error => setActionError(benefitError(error)),
  });

  const forbidden = [overview.error, preview.error, save.error, end.error].some(isBenefitForbidden);
  if (forbidden) return <div className="card p-5" dir="rtl"><EmptyState icon={<FileText className="h-8 w-8" />}
    title="אין הרשאה לניהול הטבות" description="ניהול הטבות מחיר זמין למנהלי הפלטפורמה בלבד." /></div>;
  if (overview.isLoading) return <Loading text="טוען הטבות מחיר..." />;
  if (overview.isError || !overview.data) return <div className="card p-5" dir="rtl"><p role="alert">{benefitError(overview.error)}</p>
    <Button className="mt-3 min-h-11" variant="secondary" onClick={() => void overview.refetch()}>ניסיון נוסף</Button></div>;
  const data = overview.data;
  const previewCurrent = input !== null && inputKey === JSON.stringify(previewInput) && !preview.isFetching && preview.data !== undefined && !preview.isError;
  const canSave = previewCurrent && draft.name.trim() !== '' && draft.note.trim() !== '' && !save.isPending;
  const change = <K extends keyof Draft>(key: K, value: Draft[K]) => { setDraft(old => ({ ...old, [key]: value })); setActionError(''); };
  const submit = (event: FormEvent) => {
    event.preventDefault(); setActionError('');
    if (!canSave || !input || !preview.data) return;
    if (preview.data.conflicts.length) setReplace({ input, preview: preview.data });
    else save.mutate({ org: orgId, input, preview: preview.data, replaceIds: [] });
  };

  return <section className="card space-y-5 p-5 text-ink-primary" dir="rtl" aria-label="הסכם מסחרי">
    <header><h3 className="text-base font-semibold">הסכם מסחרי · {data.organization_name}</h3>
      <p className="mt-1 text-xs text-ink-secondary"><bdi>{data.plan_name}</bdi> · {data.price.billing_cycle === 'yearly' ? 'שנתי' : 'חודשי'} · מחיר מחירון <bdi>{money(data.price.catalog_price_agorot)}</bdi> + מע״מ</p></header>
    <form onSubmit={submit} className="space-y-4">
      <h4 className="text-sm font-semibold">הוספת הטבה</h4>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <div><label className="mb-1 block text-xs text-ink-secondary" htmlFor="benefit-kind">סוג</label>
          <select id="benefit-kind" className={selectClass} value={draft.kind} onChange={e => change('kind', e.target.value as BenefitKind)}>
            <option value="absolute">מחיר קבוע ₪</option><option value="percent">אחוז הנחה</option><option value="amount_off">הנחה בסכום ₪</option><option value="free">חינם</option>
          </select></div>
        {draft.kind !== 'free' && <Input id="benefit-value" className="min-h-11" label={draft.kind === 'percent' ? 'ערך ההנחה (%)' : 'ערך (₪ לפני מע״מ)'}
          inputMode="decimal" value={draft.value} onChange={e => change('value', e.target.value)} required />}
        <Input id="benefit-start" className="min-h-11" label="מתי מתחיל" type="date" value={draft.start} onChange={e => change('start', e.target.value)} required />
        <div><label className="mb-1 block text-xs text-ink-secondary" htmlFor="benefit-duration">עד מתי</label>
          <select id="benefit-duration" className={selectClass} value={draft.duration} onChange={e => change('duration', e.target.value as BenefitDuration)}>
            <option value="fixed_term">עד תאריך</option><option value="billing_cycles">מספר חיובים</option><option value="until_cancelled">ללא סיום</option>
          </select></div>
        {draft.duration === 'fixed_term' && <Input id="benefit-end" className="min-h-11" label="תאריך סיום" type="date" min={draft.start} value={draft.end} onChange={e => change('end', e.target.value)} required />}
        {draft.duration === 'billing_cycles' && <Input id="benefit-cycles" className="min-h-11" label="מספר חיובים" type="number" min={1} step={1} value={draft.cycles} onChange={e => change('cycles', e.target.value)} required />}
        <Input id="benefit-name" className="min-h-11" label="שם ההטבה (הלקוח רואה)" value={draft.name} onChange={e => change('name', e.target.value)} required />
        <Input id="benefit-note" className="min-h-11" label="הערה פנימית" value={draft.note} onChange={e => change('note', e.target.value)} required />
      </div>
      <div className="rounded-lg border border-dashed border-navy-border bg-navy-deeper p-3 text-sm" aria-live="polite" aria-busy={preview.isFetching}>
        <h5 className="mb-2 font-medium">תצוגה מקדימה — חיוב הבא</h5>
        {!input ? <p className="text-ink-secondary">בחרו סוג, ערך ותקופה כדי לראות את החיוב הבא.</p>
          : !previewCurrent && !preview.isError ? <p className="text-ink-secondary">מחשב את החיוב הבא...</p>
          : preview.isError ? <div><p role="alert" className="text-danger">{benefitError(preview.error)}</p><Button variant="ghost" className="min-h-11" onClick={() => void preview.refetch()} type="button">ניסיון נוסף</Button></div>
          : preview.data && <><p className="mb-2 text-xs text-ink-secondary">לפי מנוע המחיר, ליום <bdi>{date(preview.data.priced_at)}</bdi></p>
            <Receipt price={preview.data.price} name={draft.name.trim()} />
            {!preview.data.candidate_applies_at_charge && <p className="mt-2 text-xs text-warning">ההטבה אינה חלה במועד החיוב הבא לפי התקופה שנבחרה.</p>}
            {preview.data.conflicts.length > 0 && <p className="mt-2 text-xs text-warning">קיימת הטבה פעילה. המחיר מוצג לאחר החלפתה; השמירה תדרוש אישור לסיום הקיימת כעת.</p>}
          </>}
      </div>
      {actionError && <p role="alert" className="text-sm text-danger">{actionError}</p>}
      <button type="submit" disabled={!canSave} className="min-h-11 rounded-lg bg-info px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50">{save.isPending ? 'שומר הטבה...' : 'שמירת הטבה'}</button>
    </form>
    <div className="space-y-3 border-t border-navy-border pt-4"><h4 className="text-sm font-semibold">הטבות של הארגון</h4>
      {!data.benefits.length && <p className="text-sm text-ink-secondary">עדיין אין הטבות לארגון.</p>}
      {data.benefits.map(row => <article key={row.id} className="flex flex-col gap-3 rounded-lg border border-navy-border bg-navy-deeper p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-1 text-sm"><div className="flex flex-wrap items-center gap-2"><span className="font-medium">{row.reason_customer || 'הטבת מחיר'}</span>
          <span className={`rounded-full px-2 py-0.5 text-xs ${row.can_end ? 'bg-success-dim text-success' : 'bg-navy-hover text-ink-secondary'}`}>{row.can_end ? 'פעילה' : 'הסתיימה'}</span></div>
          <p><bdi>{benefitValue(row)}</bdi></p><p className="text-xs text-ink-secondary">{row.can_end ? benefitDuration(row) : `הסתיימה ${date(row.updated_at)}`}
            {row.applies_to === 'including_addons' && ' · כולל תוספים'}
            {row.can_end && row.return_price && <> · {row.return_at ? <>מ-<bdi>{date(row.return_at)}</bdi></> : 'לאחר סיום ההטבה'} חוזר ל-<bdi>{money(row.return_price.subtotal_agorot)}</bdi> + מע״מ</>}
          </p><p className="break-words text-xs text-ink-secondary">הערה פנימית: {row.reason_internal}</p></div>
        {row.can_end && <button className="min-h-11 shrink-0 rounded px-2 text-sm font-medium text-danger hover:bg-danger-dim" aria-label={`סיום מוקדם: ${row.reason_customer}`} onClick={() => { setActionError(''); setEnding(row); }}>סיום מוקדם</button>}
      </article>)}
    </div>
    {replace && <Confirmation title="החלפת הטבה פעילה" pending={save.isPending} cancel={() => setReplace(null)}
      confirmLabel="סיום הקיימת ושמירת ההטבה" confirm={() => save.mutate({ org: orgId, input: replace.input, preview: replace.preview, replaceIds: replace.preview.conflicts.map(row => row.id) })}>
      <p className="text-sm">ההטבות הבאות יסתיימו כעת, וההטבה החדשה תישמר בתקופה שנבחרה:</p>
      <ul className="list-inside list-disc text-sm">{replace.preview.conflicts.map(row => <li key={row.id}>{row.reason_customer}</li>)}</ul>
    </Confirmation>}
    {ending && <Confirmation title="סיום מוקדם של הטבה" pending={end.isPending} cancel={() => setEnding(null)}
      confirmLabel="סיום ההטבה כעת" confirm={() => end.mutate({ org: orgId, id: ending.id })}>
      <p className="text-sm">ההטבה ״{ending.reason_customer}״ תסתיים כעת. החיוב הבא יחושב בלעדיה.</p>
      {ending.return_price && <p className="text-sm">מחיר לאחר סיום ההטבה: <bdi>{money(ending.return_price.subtotal_agorot)}</bdi> + מע״מ.</p>}
      {actionError && <p role="alert" className="text-sm text-danger">{actionError}</p>}
    </Confirmation>}
  </section>;
}
